import { Injectable } from "@angular/core";
import { AccountView } from "../../../../models/view/account/account.view";
import { Origin, SyncStatusEnum } from "../../../../models/types/general-types";
import { PartialTransactionDeleteError } from "../../../../services/store/transaction/transaction.store.collection";
import { TransactionStatusEnum } from "../../../../models/enum/transactionType";
import { GlossDateFactory } from "../../../../models/data/shared/gloss-date/gloss-date.factory";
import { GlossQuantityUtils } from "../../../../models/data/shared/gloss-quantity";
import { TransactionView } from "../../../../models/view/transaction/transaction.view";
import { TransactionStoreModel } from "../../../../models/store/transaction.store.model";
import { SourceTransactionStoreModel } from "../../../../models/store/source-transaction.store.model";
import { SourceTransactionsView } from "../../../../models/view/source-transactions/source-transactions.view";
import { enrichTransaction } from "../../../../models/view/transaction/transaction.utils";
import { AllocationUtils } from "../../../../models/store/allocations/allocation.utils";
import { TransactionDirection } from "../../../../models/enum/transactionDirection";
import { PlaidActions } from "./plaid.actions";
import type { PlaidTransaction } from "../plaid.types";
import type { CategoryView } from "../../../../models/view/category/category.view";

/**
 * Maps Plaid transaction payloads to vault transaction + source-transaction rows and
 * generates CBATs. Mirrors {@link FiskilTransactionAction} but adapts to Plaid's data shapes.
 *
 * Sign convention (Plaid): positive `amount` = outflow (money leaving), negative = inflow.
 * This is the OPPOSITE of Fiskil, so direction and the allocation amount are inverted here
 * to preserve the same vault semantics (In => positive allocation split).
 */
@Injectable({
  providedIn: "root",
})
export class PlaidTransactionAction extends PlaidActions {
  toTransactionView(
    plaidTransaction: PlaidTransaction,
    account: AccountView,
    categories?: CategoryView[],
  ): TransactionView {
    const storeModel = this.toTransactionStoreModel(plaidTransaction, account, categories);
    return new TransactionView(storeModel);
  }

  /**
   * Source-book row for reconciliation (mirrors {@link FiskilTransactionAction.toSourceTransactionView}).
   */
  toSourceTransactionView(
    accountView: AccountView,
    transactionView: TransactionView,
    categoryNames: string[],
    plaidTransaction: PlaidTransaction,
  ): SourceTransactionsView {
    const model = this.toSourceTransactionStoreModel(
      accountView,
      transactionView,
      categoryNames,
      plaidTransaction,
    );
    return new SourceTransactionsView(model);
  }

  private toSourceTransactionStoreModel(
    accountView: AccountView,
    transaction: TransactionView,
    categoryNames: string[],
    plaidTransaction: PlaidTransaction,
  ): SourceTransactionStoreModel {
    const postIso =
      transaction.postDate?.toISOString() ??
      transaction.glossPostDate?.getEnrichedDate()?.toISOString() ??
      new Date().toISOString();
    return {
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      id: crypto.randomUUID(),
      v: 2,
      vid: "",
      acId: transaction.accountLink.id,
      sym: accountView.currency,
      ccy: accountView.currency,
      dsc: transaction.description,
      knd: transaction.kind,
      dir: transaction.direction,
      bIB: transaction.bankImportedBalance ?? 0,
      qty: transaction.quantity.actualQuantity.amount.toString(),
      prc: 0,
      cts: categoryNames,
      cls: [],
      srId: transaction.id,
      dfn: transaction.definition,
      tDt: postIso,
      rw: JSON.stringify(plaidTransaction),
    };
  }

  private toTransactionStoreModel(
    plaidTransaction: PlaidTransaction,
    account: AccountView,
    categories?: CategoryView[],
  ): TransactionStoreModel {
    const amount = Number(plaidTransaction.amount);
    // Plaid: positive = outflow (Out), negative = inflow (In). Opposite of Fiskil.
    const direction = amount > 0 ? TransactionDirection.Out : TransactionDirection.In;
    // Plaid `date` is the posted date (YYYY-MM-DD); use it for both post and transaction date.
    const dateISO = plaidTransaction.date ?? new Date().toISOString();
    const currency = plaidTransaction.iso_currency_code ?? account.currency ?? "USD";
    // Invert the sign so vault allocation semantics match Fiskil (In => positive split).
    const allocationAmount = -amount;

    return {
      // TODO: Add providerTransactionId to TransactionStoreModel and use it for dedup. For now id stores plaid transaction_id.
      id: plaidTransaction.transaction_id,
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      v: 9,
      vid: "",
      acId: account.id,
      ori: Origin.plaid,
      bIB: undefined,
      bIBO: undefined,
      alc: AllocationUtils.createAllocationsFromCategoriesAndClassifications(
        categories ?? [],
        [],
        allocationAmount,
      ).map((a) => a.toAllocationAsObjectType()),
      dfn: TransactionStatusEnum.transaction,
      dir: direction,
      dsc: plaidTransaction.name,
      knd: "",
      lTid: [],
      xLid: [],
      sts: plaidTransaction.pending ? "pending" : "posted",
      qty: {
        cvrt: 1,
        cvsym: "",
        ccy: currency,
        qty: {
          amt: Math.abs(amount),
          prcs: 8,
          sym: currency,
        },
      },
      srId: null,
      tDt: GlossDateFactory.createFromISOString(dateISO, false, null).toGlossDateAsObject(),
      pDt: GlossDateFactory.createFromISOString(dateISO, false, null).toGlossDateAsObject(),
      vlPc: 0,
      del: false,
    };
  }

  async saveCBAT(account: AccountView, balanceOnAccount: number): Promise<boolean> {
    const silent = this.glossSyncService.isSilentBootstrap();
    try {
      if (!silent) {
        this.appState.startProcess("importing-data", `Generating CBAT of ${account.name}`);
      }
      // Read closing rows from the transaction store, not the enriched collection: enrich stays null
      // until generate() runs (deferred for the whole sync while refresh is blocked) and clearStore()
      // resets it, so an enrich read here throws before the new CBAT is saved. The store holds the same
      // closing rows; a null store just means no stale CBATs to delete, and the new CBAT still saves. #5530.
      const accountCBATTransactions = (this.transactionStore.transaction.transactionViews() ?? [])
        .filter(
          (t) => t.accountLink?.id === account.id && t.definition === TransactionStatusEnum.closing,
        )
        .map((t) => {
          const clone = t.clone();
          clone.isDeleted = true;
          return clone;
        });

      const cBat = this.newPlaidCBAT(balanceOnAccount, account);
      await this.transactionStore.transaction.save(cBat);
      await this.transactionStore.transaction.deleteFromVault(accountCBATTransactions);

      if (!silent && this.appState.activeProcesses.has("importing-data")) {
        this.appState.endProcess("importing-data");
      }
      return true;
    } catch (error) {
      // saveCBAT deletes the stale closing-balance rows; a PartialTransactionDeleteError means that
      // delete half-completed. Log the underlying `cause` (the real failure) and flag the partial
      // state rather than discarding both behind the generic wrapper. #4442 / #4504 / #4702.
      const cause = error instanceof PartialTransactionDeleteError ? error.cause : error;
      await this.glossSyncService.logError(
        [account],
        cause,
        error instanceof PartialTransactionDeleteError
          ? "Creating CBAT (partial delete)"
          : "Creating CBAT",
        SyncStatusEnum.cantSaveCBAT,
      );
      if (!silent && this.appState.activeProcesses.has("importing-data")) {
        this.appState.endProcess("importing-data");
      }
      return false;
    }
  }

  private newPlaidCBAT(balanceOnAccount: number, account: AccountView): TransactionView {
    const transaction = new TransactionView();
    transaction.glossPostDate = GlossDateFactory.createFromDate(new Date());
    transaction.bankImportedBalance = balanceOnAccount;
    transaction.bankImportedBalanceOrigin = Origin.plaid;
    transaction.quantity = GlossQuantityUtils.fromModel({
      cvrt: 1,
      ccy: account.currency ?? "USD",
      cvsym: "",
      qty: {
        amt: 0,
        sym: account.currency ?? "USD",
        prcs: 8,
      },
    });
    transaction.sourceLink = { id: null };
    transaction.accountLink = { id: account.id };
    transaction.allocations = AllocationUtils.createDefaultAllocations(0);
    transaction.description = `CBAT on Plaid sync (Balance on Account: ${balanceOnAccount})`;
    transaction.valuationPrice = null;
    transaction.linkedTo = [];
    transaction.definition = TransactionStatusEnum.closing;
    transaction.kind = "";

    enrichTransaction(transaction);
    return transaction;
  }
}
